import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { Bell, Check, Trash2, Calendar, Home, FileText, User, Wifi, WifiOff } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import { subscribePooled } from '../../../utils/realtimePoolManager';


const NotificationCenter = ({ onStatsUpdate }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterRead, setFilterRead] = useState('all');
  const [isLive, setIsLive] = useState(false);
  const [newCount, setNewCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);

      let query = supabase?.from('notifications')?.select(`
          *,
          recipient:user_profiles(full_name, email)
        `);

      if (filterType !== 'all') {
        query = query?.eq('type', filterType);
      }

      if (filterRead === 'unread') {
        query = query?.eq('is_read', false);
      } else if (filterRead === 'read') {
        query = query?.eq('is_read', true);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;

      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterRead]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Real-time subscription
  useEffect(() => {
    const unsubscribe = subscribePooled({
      channelName: 'notification-center-realtime',
      listeners: [
        { event: 'INSERT', schema: 'public', table: 'notifications' },
        { event: 'UPDATE', schema: 'public', table: 'notifications' },
        { event: 'DELETE', schema: 'public', table: 'notifications' },
      ],
      debounceMs: 300,
      onStatusChange: (status) => setIsLive(status === 'SUBSCRIBED'),
      callback: (batch) => {
        const inserts = batch?.filter((p) => p?.eventType === 'INSERT')?.length;
        if (inserts > 0) {
          setNewCount((c) => c + inserts);
        }
        fetchNotifications();
        onStatsUpdate?.();
      },
    });

    return () => { unsubscribe(); };
  }, [fetchNotifications, onStatsUpdate]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notificationId);

      if (error) throw error;

      await fetchNotifications();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('is_read', false);

      if (error) throw error;

      await fetchNotifications();
      onStatsUpdate?.();
      alert('Toutes les notifications ont été marquées comme lues');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette notification ?')) return;

    try {
      const { error } = await supabase?.from('notifications')?.delete()?.eq('id', notificationId);

      if (error) throw error;

      await fetchNotifications();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment':
        return Calendar;
      case 'property_update':
        return Home;
      case 'document_ready':
        return FileText;
      default:
        return Bell;
    }
  };

  const typeLabels = {
    appointment: 'Rendez-vous',
    property_update: 'Mise à jour propriété',
    document_ready: 'Document prêt',
    general: 'Général'
  };

  const typeColors = {
    appointment: 'bg-blue-100 text-blue-800',
    property_update: 'bg-green-100 text-green-800',
    document_ready: 'bg-purple-100 text-purple-800',
    general: 'bg-gray-100 text-gray-800'
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900">Centre de Notifications</h2>
            {isLive ? (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full">
                <Wifi className="h-3 w-3 text-green-500" />
                <span className="text-[10px] font-semibold text-green-600">Live</span>
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-full">
                <WifiOff className="h-3 w-3 text-gray-400" />
                <span className="text-[10px] text-gray-400">Connexion…</span>
              </span>
            )}
            {newCount > 0 && (
              <button
                onClick={() => { setNewCount(0); fetchNotifications(); }}
                className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-[10px] font-semibold text-blue-600 hover:bg-blue-100 transition-colors"
              >
                <Bell className="h-3 w-3" />
                {newCount} nouveau{newCount > 1 ? 'x' : ''}
              </button>
            )}
          </div>
          <p className="text-sm text-gray-600">
            Gérez et organisez toutes les notifications système avec outils de communication ciblée
          </p>
        </div>
        <button
          onClick={handleMarkAllAsRead}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Check className="h-4 w-4" />
          <span>Tout marquer lu</span>
        </button>
      </div>
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mr-2">Type:</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les types</option>
              <option value="appointment">Rendez-vous</option>
              <option value="property_update">Mise à jour propriété</option>
              <option value="document_ready">Document prêt</option>
              <option value="general">Général</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mr-2">Statut:</label>
            <select
              value={filterRead}
              onChange={(e) => setFilterRead(e?.target?.value)}
              className="px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Toutes</option>
              <option value="unread">Non lues</option>
              <option value="read">Lues</option>
            </select>
          </div>

          <div className="text-sm text-gray-600">
            <span>{notifications?.length} notification(s) trouvée(s)</span>
          </div>
        </div>
      </div>
      {/* Notifications List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des notifications...</p>
          </div>
        ) : notifications?.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-sm border text-center text-gray-500">
            <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Aucune notification trouvée</p>
          </div>
        ) : (
          notifications?.map((notification) => {
            const Icon = getNotificationIcon(notification?.type);
            return (
              <div
                key={notification?.id}
                className={`bg-white p-4 rounded-lg shadow-sm border-l-4 transition-all hover:shadow-md ${
                  notification?.is_read 
                    ? 'border-l-gray-300 opacity-75' :'border-l-blue-400'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 p-2 rounded-full ${
                    notification?.is_read ? 'bg-gray-100' : 'bg-blue-100'
                  }`}>
                    <Icon className={`h-5 w-5 ${
                      notification?.is_read ? 'text-gray-500' : 'text-blue-600'
                    }`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className={`text-sm font-semibold ${
                            notification?.is_read ? 'text-gray-700' : 'text-gray-900'
                          }`}>
                            {notification?.title}
                          </h3>
                          {!notification?.is_read && (
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                          )}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${typeColors?.[notification?.type] || 'bg-gray-100 text-gray-800'}`}>
                            {typeLabels?.[notification?.type] || notification?.type}
                          </span>
                        </div>
                        <p className={`text-sm ${
                          notification?.is_read ? 'text-gray-600' : 'text-gray-700'
                        }`}>
                          {notification?.message}
                        </p>
                        
                        {notification?.recipient && (
                          <div className="flex items-center space-x-2 mt-2 text-xs text-gray-500">
                            <User className="h-3 w-3" />
                            <span>Pour: {notification?.recipient?.full_name}</span>
                            <span>({notification?.recipient?.email})</span>
                          </div>
                        )}

                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span>
                            {new Date(notification?.created_at)?.toLocaleString('fr-FR')}
                          </span>
                          {notification?.entity_type && notification?.entity_id && (
                            <span className="flex items-center space-x-1">
                              <span>Référence:</span>
                              <span className="font-mono bg-gray-100 px-1 rounded">
                                {notification?.entity_type}:{notification?.entity_id?.slice(0, 8)}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        {!notification?.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(notification?.id)}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                            title="Marquer comme lu"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteNotification(notification?.id)}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      {/* Quick Actions */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Actions Rapides</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 border rounded-lg text-center">
            <div className="text-lg font-semibold text-blue-600">
              {notifications?.filter(n => !n?.is_read)?.length}
            </div>
            <div className="text-sm text-gray-600">Non lues</div>
          </div>
          <div className="p-3 border rounded-lg text-center">
            <div className="text-lg font-semibold text-green-600">
              {notifications?.filter(n => n?.type === 'appointment')?.length}
            </div>
            <div className="text-sm text-gray-600">Rendez-vous</div>
          </div>
          <div className="p-3 border rounded-lg text-center">
            <div className="text-lg font-semibold text-purple-600">
              {notifications?.filter(n => n?.type === 'document_ready')?.length}
            </div>
            <div className="text-sm text-gray-600">Documents</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;